import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { DialogBox, Button } from 'xm-spark';

import { useRedirectAutomaticallyToAllEvents } from 'hooks/useRedirectAutomaticallyToAllEvents';
import { getNicePlaceLocation, getRouterPath } from 'utils/navigation';
import HelpLink from 'Application/HelpLinks/HelpLink';

import EventRedirectAlert from 'components/EventRedirectAlert';
import classnames from 'classnames';
import ReportsGraphic from 'resources/svg/reports-graphic.svg';
import { useGetEventCreatedAt } from './hooks';

import styles from './RecentEventsTrackingRedirect.module.scss';

const recentEventsTabToAllEventsTab = {
  trackingReport: 'overview',
  eventAttachmentsReport: 'overview',
  conferenceBridgeManagement: 'conference',
  eventSuppressedDuplicates: 'suppression',
  personDelivery: 'userdelivery',
  eventReportCard: 'overview',
  eventDetailsMessage: 'message',
  eventTimeline: 'timeline',
  eventLogReport: 'log',
  eventPropertyReport: 'overview',
};

const RecentEventsTrackingRedirect = () => {
  const { eventUuid } = useParams();
  const [showDialogBox, setShowDialogBox] = useState(true);

  const allEventsPath = `EventDetailsReportPlace:/${eventUuid}`;
  const createdAt = useGetEventCreatedAt(eventUuid);
  const encodedCreatedAt = encodeURIComponent(createdAt?.replace('+0000', 'Z'));

  const redirectAutomaticallyToAllEvents = useSelector(
    state => state.allEvents.redirectAutomaticallyToAllEvents,
  );
  const setRedirectAutomaticallyToAllEvents =
    useRedirectAutomaticallyToAllEvents();

  const getAllEventsTab = () => {
    const currentPlace = getNicePlaceLocation();
    const tabName = recentEventsTabToAllEventsTab[currentPlace];
    const filters =
      tabName === 'userdelivery'
        ? '{"filterBy":{"NONE":""},%20"sortBy":{"DELIVERY_DATE":"Ascending"}}'
        : '';
    return getRouterPath(
      `/${allEventsPath}/${tabName}/filters=${filters}/${encodedCreatedAt}`,
    );
  };

  const redirectToAllEvents = redirect => {
    setShowDialogBox(false);
    setRedirectAutomaticallyToAllEvents(redirect);
  };

  setRedirectAutomaticallyToAllEvents(true);

  return (
    <>
      <DialogBox
        isOpen={showDialogBox && redirectAutomaticallyToAllEvents === undefined}
        onClose={() => redirectToAllEvents(false)}
        titleText={<FormattedMessage id="RECENT_EVENTS_REDIRECT_MODAL_TITLE" />}
        rightButtons={[
          <Button
            className={classnames('button-primary')}
            key="new"
            onClick={() => redirectToAllEvents(true)}
          >
            <FormattedMessage id="RECENT_EVENTS_REDIRECT_NEW_VIEW_BUTTON" />
          </Button>,
          <Button key="old" onClick={() => redirectToAllEvents(false)}>
            <FormattedMessage id="RECENT_EVENTS_REDIRECT_OLD_VIEW_BUTTON" />
          </Button>,
        ]}
        classes={{
          wrap: classnames(styles.modalDialog),
          content: styles.content,
        }}
      >
        <ReportsGraphic className={styles.graphic} />
        <FormattedMessage id="RECENT_EVENTS_REDIRECT_MODAL_TEXT" />
        <HelpLink helpKey="EventReport">
          <FormattedMessage id="RECENT_EVENTS_REDIRECT_LEARN_MORE" />
        </HelpLink>
      </DialogBox>

      {createdAt &&
        (redirectAutomaticallyToAllEvents ? (
          <Redirect to={getAllEventsTab()} />
        ) : (
          <EventRedirectAlert
            eventId={eventUuid}
            eventType="RECENT"
            redirectLink={getAllEventsTab()}
          />
        ))}
    </>
  );
};

RecentEventsTrackingRedirect.displayName = 'RecentEventsTrackingRedirect';

export default RecentEventsTrackingRedirect;
